import { u8concat } from "@/lib/binary";


export function prefixLengthToBuffer(buffer: Uint8Array) {
  const length = buffer.byteLength;
  const lengthBuffer = new Uint8Array(4);
  new DataView(lengthBuffer.buffer).setUint32(0, length);
  return u8concat(lengthBuffer, buffer);
}

/**
 * Stream length-prefixed buffers from a ReadableStreamDefaultReader
 * 
 * Enhanced with robust error handling for incomplete chunked encoding,
 * retryable operations, and graceful recovery from transient network errors
 * 
 * @param reader The ReadableStreamDefaultReader instance to read from
 * @returns An async generator yielding complete buffer frames
 */
export async function* streamLengthPrefixedBuffers(
  reader: ReadableStreamDefaultReader<Uint8Array>
) {
  let retryCount = 0;
  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 1000; // 1 second initial delay with exponential backoff
  
  try {
    let b = new Uint8Array()

    while (true) {
      try {
        // Read length prefix (4 bytes)
        while (b.byteLength < 4) {
          const { done, value } = await reader.read();
          if (done) {
            console.warn("Reader ended prematurely while reading length - connection closed");
            return; // End generator gracefully
          }

          b = u8concat(b, value);
        }

        const length = new DataView(b.buffer, b.byteOffset, 4).getUint32(0);
        
        // Sanity check length to protect against corrupted data
        if (length > 10_000_000) { // 10MB max message size
          console.error(`Potentially corrupted length prefix: ${length} bytes. Resetting buffer.`);
          b = new Uint8Array(); // Reset buffer to recover
          continue; // Try again with fresh buffer
        }

        b = b.subarray(4);

        // Read the actual message frame
        while (b.byteLength < length) {
          const { done, value } = await reader.read();
          if (done) {
            console.warn("Reader ended prematurely while reading frame - incomplete data");
            return; // End generator gracefully
          }

          b = u8concat(b, value);
        }

        // Reset retry count on successful read
        retryCount = 0;
        
        yield b.subarray(0, length);
        b = b.subarray(length);
      } catch (err) {
        // Handle ERR_INCOMPLETE_CHUNKED_ENCODING and other transient errors
        if (err instanceof Error) {
          const errorMessage = err.message || String(err);
          const isIncompleteChunkedError = errorMessage.includes("ERR_INCOMPLETE_CHUNKED_ENCODING") || 
                                         errorMessage.includes("ended prematurely") ||
                                         errorMessage.includes("network error");
          
          if (isIncompleteChunkedError && retryCount < MAX_RETRIES) {
            retryCount++;
            const delayMs = RETRY_DELAY_MS * Math.pow(2, retryCount - 1); // Exponential backoff
            
            console.warn(
              `Recoverable stream error encountered (retry ${retryCount}/${MAX_RETRIES}), ` +
              `retrying in ${delayMs}ms: ${errorMessage}`
            );
            
            // Wait with exponential backoff before retry
            await new Promise(resolve => setTimeout(resolve, delayMs));
            
            // Reset buffer and try again
            b = new Uint8Array();
            continue;
          }
        }
        
        // Non-recoverable error - propagate it
        throw err;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export async function readRequestBodyBuffer(req: Request, maxSize: number) {
  if (req.body === null) {
    throw new Error("Request body is null");
  }

  const contentLength = req.headers.get("content-length");
  if (contentLength === null) {
    throw new Error("Content-Length header is missing");
  }

  const size = parseInt(contentLength);
  if (isNaN(size)) {
    throw new Error("Content-Length header is not a number");
  }

  if (size > maxSize) {
    throw new Error("Content-Length header is greater than maxSize");
  }

  const reader = req.body.getReader();

  try {
    let buffer = new Uint8Array();

    while (buffer.byteLength < size) {
      const { done, value } = await reader.read();
      if (done) {
        throw new Error("Request body ended prematurely");
      }

      if (buffer.byteLength + value.byteLength > size) {
        throw new Error("Request body is larger than size");
      }

      buffer = u8concat(buffer, value);
    }
    return buffer;
  } finally {
    reader.releaseLock();
  }
}
